<template>
  <!-- 模板部分无任何修改，保持原样 -->
  <div class="login-container">
    <div class="login-card">
      <div class="login-header">
        <i class="el-icon-user-solid"></i>
        <h1>复课证明</h1>
      </div>
      <el-tabs v-model="activeTab" class="login-tabs">
        <!-- 学生登录 -->
        <el-tab-pane label="学生登录" name="student">
          <el-form :model="studentForm" :rules="studentRules" ref="studentFormRef" label-width="100px"
            label-position="left" class="login-form">
            <el-form-item label="身份证号" prop="idCard">
              <el-input v-model="studentForm.idCard" placeholder="请输入身份证号" type="text" maxlength="18" show-word-limit
                @blur="checkIdCard" autocomplete="off"></el-input>
            </el-form-item>
            <el-form-item label="学生姓名" prop="name">
              <el-input v-model="studentForm.name" placeholder="请输入学生姓名" type="text" show-word-limit
                autocomplete="off"></el-input>
            </el-form-item>
            <el-form-item label="学校" prop="school">
              <el-select v-model="studentForm.school" placeholder="请选择学校" class="school-select" filterable clearable>
                <el-option v-for="school in schoolList" :key="school.id" :label="school.schName"
                  :value="school.schName"></el-option>
              </el-select>
              <div class="school-tip" v-if="hopId === '1'">
                <p>
                  <i class="el-icon-info"></i>
                  仅支持兴隆街道范围内学校开具复课证明，如未找到学生学校，请线下办理或咨询校医前往对应基层医疗机构开具证明。
                </p>
              </div>
            </el-form-item>
            <el-form-item class="login-btn-item">
              <el-button type="primary" @click="handleStudentLogin" class="login-btn" :loading="submissionLoading">
                <span v-if="!submissionLoading">提交</span>
                <span v-if="submissionLoading">提交中...</span>
              </el-button>
            </el-form-item>
          </el-form>
        </el-tab-pane>
        <!-- 医生登录 -->
        <el-tab-pane label="医生登录" name="doctor">
          <el-form :model="doctorForm" :rules="doctorRules" ref="doctorFormRef" label-width="100px"
            label-position="left" class="login-form">
            <el-form-item label="手机号" prop="phone">
              <el-input v-model="doctorForm.phone" placeholder="请输入手机号" type="text" maxlength="11" show-word-limit
                autocomplete="off"></el-input>
            </el-form-item>
            <el-form-item label="密码" prop="password">
              <el-input v-model="doctorForm.password" placeholder="请输入密码" type="password" autocomplete="off"></el-input>
            </el-form-item>
            <el-form-item class="login-btn-item">
              <el-button type="primary" @click="handleDoctorLogin" class="login-btn" :loading="submissionLoading">
                <span v-if="!submissionLoading">登录</span>
                <span v-if="submissionLoading">登录中...</span>
              </el-button>
            </el-form-item>
          </el-form>
        </el-tab-pane>
      </el-tabs>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import request from '@/utils/request';
// 补充导入全局Token和医生Token的操作方法（需在auth.js中新增）
import { setGlobalToken, getGlobalToken, removeGlobalToken, setToken, getToken, removeToken } from '@/utils/auth';
import store from '@/store';

// 路由实例
const route = useRoute();
const router = useRouter();

// 响应式状态
const activeTab = ref('student');
const submissionLoading = ref(false);
const idCardCheckLoading = ref(false);
const schoolList = ref([]);
const existingQueId = ref(null);
const hopId = ref('');
// 标记是否获取到学生接口的全局Token
const hasGlobalToken = ref(false);

// 表单Ref
const studentFormRef = ref(null);
const doctorFormRef = ref(null);

// 学生表单数据（保持原代码不变）
const studentForm = reactive({
  name: '',
  idCard: '',
  school: '',
  grade: '',
  class: '',
  gender: '',
  address: '',
  parentName: '',
  parentPhone: '',
  relationship: '',
  fallTime: '',
  diagnosisTime: '',
  disease: '',
  symptoms: '',
  symptomsOther: '',
  symptomsTime: '',
  hospital: '',
  parentUrl: '',
  queId: ''
});

// 学生表单校验规则（保持原代码不变）
const studentRules = ref({
  name: [
    { required: true, message: '请输入学生姓名', trigger: 'blur' },
    { pattern: /^[\u4e00-\u9fa5a-zA-Z]+$/, message: '姓名仅支持中文和英文', trigger: 'blur' },
    { min: 2, max: 10, message: '姓名长度为2-10位', trigger: 'blur' }
  ],
  idCard: [
    { required: true, message: '请输入身份证号', trigger: 'blur' },
    { pattern: /(^\d{18}$)|(^\d{17}(\d|X|x)$)/, message: '请输入18位有效身份证号', trigger: 'blur' }
  ],
  school: [
    { required: true, message: '请选择学校', trigger: 'change' }
  ]
});

// 医生表单数据（保持原代码不变）
const doctorForm = reactive({
  phone: '',
  password: ''
});

// 医生表单校验规则（保持原代码不变）
const doctorRules = ref({
  phone: [
    { required: true, message: '请输入手机号', trigger: 'blur' },
    { pattern: /^1[3-9]\d{9}$/, message: '请输入有效的手机号', trigger: 'blur' }
  ],
  password: [
    { required: true, message: '请输入密码', trigger: 'blur' }
  ]
});

/**
 * 组件挂载时执行
 */
onMounted(() => {
  // 1. 解析URL中的hopId参数
  parseHopIdFromUrl();

  // 2. 检查是否已登录（保持原逻辑）
  // const userInfo = localStorage.getItem('userInfo');
  // if (userInfo) {
  //   const redirect = route.query.redirect || (JSON.parse(userInfo).role === 'doctor' ? '/yishengshenhe' : '/survey');
  //   router.push(redirect);
  //   return;
  // }

  // // 3. 从本地缓存获取全局Token（学生用）
  // const localGlobalToken = getGlobalToken();
  // if (localGlobalToken) {
  //   hasGlobalToken.value = true;
  //   console.log('从本地缓存获取到学生全局Token：', localGlobalToken);
  // }

  // 4. 获取学校列表（从message提取全局Token）
  fetchSchoolList();
});

/**
 * 解析URL中的hopId参数（保持原代码不变）
 */
const parseHopIdFromUrl = () => {
  const queryHopId = route.query.hopId || localStorage.getItem('hopId') || '';
  if (queryHopId && /^\d+$/.test(queryHopId)) {
    hopId.value = queryHopId;
    localStorage.setItem('hopId', queryHopId);
    console.log('成功解析并存储hopId：', hopId.value);
  } else {
    console.warn('未检测到有效的hopId参数，将无法正常获取学校列表，请检查访问链接！')
    // ElMessageBox.warning('未检测到有效的hopId参数，将无法正常获取学校列表，请检查访问链接！', '参数异常', {
    //   confirmButtonText: '确定',
    //   type: 'warning'
    // });
    hopId.value = '';
  }
};

/**
 * 获取学校列表：从message提取学生全局Token并存储
 */
const fetchSchoolList = () => {
  if (!hopId.value) {
    ElMessage.error('获取学校列表失败：缺少有效的hopId参数');
    return;
  }

  request({
    url: '/api/schoolInfos/all',
    method: 'get',
    params: {
      hopId: hopId.value
    },
    // 标记该请求不携带任何Token（首次获取Token的请求无需鉴权）
    headers: {
      'skip-token': 'true'
    }
  }).then(response => {
    // 1. 提取message中的学生全局Token并存储
    if (response.message) {
      const globalToken = response.message;
      setGlobalToken(globalToken); // 存储学生全局Token
      hasGlobalToken.value = true;
      console.log('从学校接口message中获取并存储学生全局Token：', globalToken);
    } else {
      ElMessage.warning('学校接口未返回学生全局Token，部分功能可能受限！');
      hasGlobalToken.value = false;
      removeGlobalToken();
    }

    // 2. 处理学校列表数据
    if (response.code === 200 && Array.isArray(response.data)) {
      schoolList.value = response.data;
    } else {
      ElMessage.error('获取学校列表失败：' + (response.msg || response.message || '服务器返回异常'));
    }
  }).catch(error => {
    ElMessage.error('网络异常，获取学校列表失败！');
    console.error('获取学校列表报错：', error);
    hasGlobalToken.value = false;
    removeGlobalToken();
  });
};

/**
 * 身份证号校验：仅校验学生全局Token，医生逻辑不影响
 */
const checkIdCard = () => {
  // 学生操作需校验全局Token
  if (!hasGlobalToken.value && !getGlobalToken()) {
    ElMessage.error('未获取到学生认证Token，无法校验身份证号！');
    return;
  }

  const idCard = studentForm.idCard.trim();
  existingQueId.value = null;

  if (!idCard || !/(^\d{18}$)|(^\d{17}(\d|X|x)$)/.test(idCard)) {
    studentForm.name = '';
    studentForm.school = '';
    return;
  }

  idCardCheckLoading.value = true;
  request({
    url: '/api/questionnaires/getSfzAndHopId',
    method: 'post',
    params: { childrenSfz: idCard ,hopId:localStorage.getItem('hopId')},
    // 标记该请求携带学生全局Token
    meta: { tokenType: 'global' }
  }).then(response => {
    idCardCheckLoading.value = false;
    if (response.code === 200 && response.data) {
      existingQueId.value = response.data.queId;
      studentForm.name = response.data.queChildrenName || '';
      studentForm.school = response.data.queChildrenSchool || '';
      ElMessage.success('已查询到学生信息');
    } else {
      existingQueId.value = null;
      studentForm.name = '';
      studentForm.school = '';
      if (response.code === 500) {
        ElMessage.warning('未查询到学生信息，请手动填写姓名和学校');
      } else {
        ElMessage.error('身份证号验证失败：' + (response.message || '未知错误'));
      }
    }
  }).catch(error => {
    idCardCheckLoading.value = false;
    ElMessage.error('网络异常，身份证号验证失败！');
    console.error(error);
  });
};

/**
 * 学生提交逻辑：仅校验学生全局Token，医生逻辑不变
 */
const handleStudentLogin = async () => {
  // 学生操作需校验全局Token
  if (!hasGlobalToken.value && !getGlobalToken()) {
    ElMessage.error('未获取到学生认证Token，无法提交学生信息！');
    return;
  }

  if (!studentFormRef.value) return;

  try {
    await studentFormRef.value.validate();
    submissionLoading.value = true;

    const postData = {
      queChildrenSfz: studentForm.idCard.trim(),
      queChildrenName: studentForm.name.trim(),
      queChildrenSchool: studentForm.school,
      queId: existingQueId.value
    };

    const response = await request({
      url: '/api/questionnaires/addOrUpdate',
      method: 'post',
      data: postData,
      // 标记该请求携带学生全局Token
      meta: { tokenType: 'global' }
    });

    if (response.code === 200 && response.data) {
      // 学生原Token逻辑（exam202510）可保留，仅作为业务字段，鉴权用全局Token
      const studentToken = response.data.exam202510;
      if (studentToken) {
        // 若需保留学生业务Token，可存储但不用于鉴权
        localStorage.setItem('student_biz_token', studentToken);
      }

      const userInfo = {
        ...response.data,
        queChildrenGrade: response.data.queChildrenGrade || studentForm.grade.trim(),
        queChildrenClass: response.data.queChildrenClass || studentForm.class.trim(),
        queChildrenGender: response.data.queChildrenGender || studentForm.gender.trim(),
        queChildrenAddress: response.data.queChildrenAddress || studentForm.address.trim(),
        queParentName: response.data.queParentName || studentForm.parentName.trim(),
        quePhone: response.data.quePhone || studentForm.parentPhone.trim(),
        queChildrenRelationship: response.data.queChildrenRelationship || studentForm.relationship.trim(),
        queFallTime: response.data.queFallTime || studentForm.fallTime.trim(),
        queDiagnosisTime: response.data.queDiagnosisTime || studentForm.diagnosisTime.trim(),
        queDisease: response.data.queDisease || studentForm.disease.trim(),
        queSymptoms: response.data.queSymptoms || studentForm.symptoms.trim(),
        queSymptomsOther: response.data.queSymptomsOther || studentForm.symptomsOther.trim(),
        queSymptomsTime: response.data.queSymptomsTime || studentForm.symptomsTime.trim(),
        queHospital: response.data.queHospital || studentForm.hospital.trim(),
        queParentUrl: response.data.queParentUrl || studentForm.parentUrl.trim(),
        role: 'student'
      };
      localStorage.setItem('userInfo', JSON.stringify(userInfo));
      localStorage.setItem('queId', userInfo.queId);
      localStorage.setItem('queChildrenSfz', userInfo.queChildrenSfz);
      localStorage.setItem('queChildrenName', userInfo.queChildrenName);
      localStorage.setItem('role', 'student');
      localStorage.setItem('bingliCompleted', 'false');
      localStorage.setItem('xinxiCompleted', 'false');
      localStorage.setItem('zhengmingCompleted', 'false');
      localStorage.setItem('uploadedFilePath', userInfo.queChildrenFile || '');

      if (existingQueId.value) {
        ElMessage.success('学生信息已更新，请继续填写问卷。');
        router.push('/survey');
      } else {
        ElMessage.success('学生信息已创建，请阅读并签署复课证明同意书。');
        router.push('/consent');
      }
    } else {
      ElMessage.error('操作失败：' + (response.message || '服务器内部错误'));
    }
  } catch (error) {
    console.error('学生提交失败:', error);
    ElMessage.error(error.message || '表单验证失败，请检查输入内容');
  } finally {
    submissionLoading.value = false;
  }
};

/**
 * 医生登录逻辑：完全保持原代码不变！！！
 */
const handleDoctorLogin = async () => {
  if (!doctorFormRef.value) return;

  try {
    await doctorFormRef.value.validate();
    submissionLoading.value = true;

    const loginParams = {
      phone: doctorForm.phone.trim(),
      password: doctorForm.password.trim()
    };

    const response = await request({
      url: '/api/physicians/checkLogin',
      method: 'post',
      params: loginParams,
      // 标记该请求携带医生Token
      meta: { tokenType: 'doctor' }
    });

    if (response.code === 200 && response.data) {
      const doctorToken = response.data;
      setToken(doctorToken); // 存储医生专属Token

      const userInfo = JSON.stringify({
        phone: loginParams.phone,
        role: 'doctor'
      });
      localStorage.setItem('userInfo', userInfo);
      localStorage.setItem('physicianPhone', loginParams.phone);

      store.commit('setUserRoles', ['doctor']);
      console.log('Vuex 角色设置:', store.getters.userRoles);

      ElMessage.success(response.message || '医生登录成功！');

      setTimeout(() => {
        router.replace('/yishengshenhe');
      }, 300);
    } else {
      ElMessage.error(`登录失败：${response.message || '服务器校验失败'}`);
    }
  } catch (error) {
    console.error('医生登录失败:', error);
    ElMessage.error(error.message || '网络异常，登录失败！');
  } finally {
    submissionLoading.value = false;
  }
};
</script>

<style scoped>
.login-container {
  width: 100%;
  height: 100vh;
  background-image: url('../assets/beijing2.png');
  background-repeat: repeat;
  background-position: center;
  background-attachment: fixed;
  background-size: cover;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  box-sizing: border-box;
}

.login-card {
  width: 100%;
  max-width: 500px;
  background-color: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  padding: 30px 40px;
  box-sizing: border-box;
}

.login-header {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

.login-header i {
  font-size: 24px;
  color: #409eff;
  margin-right: 10px;
}

.login-header h1 {
  font-size: 22px;
  color: #333;
  font-weight: 500;
}

.login-tabs {
  margin-bottom: 10px;
}

.login-form {
  width: 100%;
  margin-top: 20px;
}

.el-input,
.school-select {
  width: 100%;
}

.school-tip {
  margin-top: 8px;
  padding: 8px 12px;
  background-color: #fef0f0;
  border-radius: 4px;
}

.school-tip p {
  font-size: 12px;
  color: #e53e3e;
  line-height: 1.5;
  margin: 0;
}

.school-tip i {
  margin-right: 4px;
}

.login-btn-item {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

.login-btn {
  width: 100%;
  height: 44px;
  font-size: 16px;
  border-radius: 8px;
}

@media (max-width: 480px) {
  .login-card {
    padding: 20px 15px;
  }

  .login-header h1 {
    font-size: 20px;
  }

  .el-form-item {
    margin-bottom: 15px;
  }

  .checking-tip {
    display: flex;
    align-items: center;
    margin-top: 8px;
    color: #409eff;
    font-size: 12px;
  }

  .checking-tip i {
    margin-right: 4px;
    animation: loading 1s linear infinite;
  }

  @keyframes loading {
    0% {
      transform: rotate(0deg);
    }

    100% {
      transform: rotate(360deg);
    }
  }
}
</style>